/**
 * Bybit Onboarding Modal
 * 
 * B2: Modal explaining how to connect Bybit safely
 * Shows API permissions, risk disclaimer, and trading toggle explanation
 */

import React, { useState } from "react";
import { Button } from "@app/components/shared/Button";
import "./BybitOnboardingModal.css";

interface BybitOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
}

export function BybitOnboardingModal({ isOpen, onClose, onContinue }: BybitOnboardingModalProps): JSX.Element | null {
  const [currentStep, setCurrentStep] = useState(1);
  const [acceptedRisk, setAcceptedRisk] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) {
    return null;
  }

  const totalSteps = 4;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      if (dontShowAgain) {
        localStorage.setItem("onboarding_dont_show", "true");
      }
      if (onContinue) {
        onContinue();
      }
      onClose();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      localStorage.setItem("onboarding_dont_show", "true");
    }
    setCurrentStep(1);
    setAcceptedRisk(false);
    onClose();
  };

  const handleClose = () => {
    setCurrentStep(1);
    setAcceptedRisk(false);
    onClose();
  };

  return (
    <div className="onboarding-modal-overlay" onClick={handleClose}>
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        <div className="onboarding-modal__header">
          <h2>How to Connect Bybit Safely</h2>
          <button className="onboarding-modal__close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="onboarding-modal__content">
          {/* Step 1: Introduction */}
          {currentStep === 1 && (
            <div className="onboarding-step">
              <h3>Welcome to Zakyam AI Trader</h3>
              <p>
                Zakyam currently trades exclusively on Bybit. You need a Bybit account to use automated trading.
              </p>
              <p>
                Before connecting your Bybit account, please read this guide carefully to ensure safe and secure integration.
              </p>
              <div className="onboarding-step__info">
                <p><strong>What you'll learn:</strong></p>
                <ul>
                  <li>Required API permissions</li>
                  <li>Security best practices</li>
                  <li>Trading controls</li>
                  <li>Risk disclaimer</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: API Permissions */}
          {currentStep === 2 && (
            <div className="onboarding-step">
              <h3>API Permissions</h3>
              <div className="onboarding-step__warning">
                <p><strong>⚠️ IMPORTANT: Use Trade-Only Permissions</strong></p>
                <p>
                  When creating your Bybit API key, you <strong>MUST</strong> select <strong>Trade</strong> permissions only.
                </p>
                <p>
                  <strong>DO NOT</strong> enable:
                </p>
                <ul>
                  <li>❌ Withdraw permissions</li>
                  <li>❌ Transfer permissions</li>
                  <li>❌ Any other permissions</li>
                </ul>
                <p>
                  <strong>✅ ONLY enable:</strong>
                </p>
                <ul>
                  <li>✅ Trade (Spot/Perpetual)</li>
                </ul>
              </div>
              <div className="onboarding-step__info">
                <p>
                  <strong>Why?</strong> This ensures that even if your API key is compromised, 
                  attackers cannot withdraw your funds. They can only trade (which still requires your account balance).
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Trading Toggle */}
          {currentStep === 3 && (
            <div className="onboarding-step">
              <h3>Trading Controls</h3>
              <p>
                You have full control over when trading is active:
              </p>
              <div className="onboarding-step__info">
                <h4>Trading Enabled/Disabled Toggle</h4>
                <p>
                  In your Trading System settings, you can enable or disable trading at any time.
                </p>
                <ul>
                  <li><strong>Trading Enabled:</strong> System can execute entry orders for new positions</li>
                  <li><strong>Trading Disabled:</strong> System will NOT execute new entry orders</li>
                </ul>
                <p className="onboarding-step__note">
                  <strong>Note:</strong> When trading is disabled, the system will still execute exit orders 
                  for existing positions. This ensures you can always close positions when needed.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Risk Disclaimer */}
          {currentStep === 4 && (
            <div className="onboarding-step">
              <h3>Risk Disclaimer</h3>
              <div className="onboarding-step__warning">
                <p><strong>⚠️ Trading Involves Risk</strong></p>
                <p>
                  Cryptocurrency trading involves substantial risk of loss. You may lose some or all of your invested capital.
                </p>
              </div>
              <div className="onboarding-step__info">
                <p><strong>Important Points:</strong></p>
                <ul>
                  <li>❌ <strong>No guarantee of profits</strong> - Past performance does not guarantee future results</li>
                  <li>❌ <strong>Not financial advice</strong> - The Service provides automated execution, not financial advice</li>
                  <li>⚠️ <strong>Technical risks</strong> - System errors, network issues, or exchange outages may affect trading</li>
                  <li>⚠️ <strong>Market volatility</strong> - Rapid price movements may result in unexpected losses</li>
                </ul>
                <p>
                  <strong>Only trade with funds you can afford to lose.</strong>
                </p>
              </div>
              <div className="onboarding-step__checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={acceptedRisk}
                    onChange={(e) => setAcceptedRisk(e.target.checked)}
                  />
                  <span>
                    I understand the risks involved in cryptocurrency trading and agree to the{" "}
                    <a href="/risk-disclaimer" target="_blank" rel="noopener noreferrer">
                      Risk Disclaimer
                    </a>
                    {" "}and{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="onboarding-modal__footer">
          <div className="onboarding-modal__progress">
            Step {currentStep} of {totalSteps}
          </div>
          <div className="onboarding-modal__actions">
            {currentStep > 1 && (
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button variant="ghost" onClick={handleSkip}>
              Skip tutorial
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={currentStep === totalSteps && !acceptedRisk}
            >
              {currentStep === totalSteps ? "I Understand, Continue" : "Next"}
            </Button>
          </div>
        </div>
        <div className="onboarding-modal__skip-options">
          <label className="onboarding-modal__dont-show-label">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            <span>Do not show this tutorial again</span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default BybitOnboardingModal;

